import React, { useEffect, useState } from "react";
import { Save, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { ImageUpload } from "../../components/ImageUpload";
import { useSite } from "../../context/SiteContext";

const SECTIONS = [
  {
    id: "brand",
    label: "BRAND / LOGO",
    items: [],
    images: [
      { key: "header_logo", label: "HEADER LOGO (transparent PNG/SVG recommended; leave empty for built-in word-mark)", rec: "200×60 transparent PNG/SVG" },
    ],
  },
  {
    id: "lock",
    label: "SITE LOCK / COMING SOON",
    items: [
      { key: "site_locked", label: "LOCK SITE", noLang: true, bool: true, hint: "When ON, the password splash gates the whole site (admins always bypass)." },
      { key: "site_lock_password", label: "PASSWORD (single, shared)", noLang: true, placeholder: { en: "Pick a passphrase" } },
      { key: "site_lock_title", label: "SPLASH TITLE", placeholder: { en: "COMING SOON.", pt: "EM BREVE." } },
      { key: "site_lock_message", label: "SPLASH MESSAGE", multiline: true, placeholder: { en: "The cult is preparing. Enter the password to continue.", pt: "O culto está a preparar-se. Insere a password para continuar." } },
    ],
    images: [
      { key: "site_lock_image", label: "SPLASH BACKGROUND IMAGE", rec: "1920×1200 (landscape)" },
    ],
  },
  {
    id: "countdown",
    label: "DROP COUNTDOWN BANNER",
    items: [
      { key: "drop_countdown_enabled", label: "ENABLED", noLang: true, bool: true, hint: "Toggle the drop countdown banner that appears at the top of every page." },
      { key: "drop_countdown_date", label: "LAUNCH DATETIME (ISO, e.g. 2026-06-12T21:00:00)", noLang: true, placeholder: { en: "2026-06-12T21:00:00" } },
      { key: "drop_countdown_label", label: "LABEL BEFORE COUNTDOWN", placeholder: { en: "INVICTA DROP", pt: "INVICTA DROP" } },
      { key: "drop_countdown_live_label", label: "LABEL WHEN LIVE", placeholder: { en: "LIVE NOW", pt: "AGORA AO VIVO" } },
      { key: "drop_countdown_link", label: "CLICK / CTA LINK (e.g. /shop?collection=wrestling)", noLang: true, placeholder: { en: "/shop" } },
    ],
    images: [],
  },
  {
    id: "nav",
    label: "NAVIGATION (label · URL · hide)",
    items: [
      { key: "nav_shop", label: "SHOP — label" },
      { key: "nav_shop_url", label: "SHOP — URL (blank = /shop)", noLang: true, placeholder: { en: "/shop or https://..." } },
      { key: "nav_shop_hidden", label: "SHOP — hide from header", noLang: true, bool: true },
      { key: "nav_tickets", label: "TICKETS — label" },
      { key: "nav_tickets_url", label: "TICKETS — URL (blank = /tickets)", noLang: true, placeholder: { en: "/tickets" } },
      { key: "nav_tickets_hidden", label: "TICKETS — hide from header", noLang: true, bool: true },
      { key: "nav_events", label: "EVENTS — label" },
      { key: "nav_events_url", label: "EVENTS — URL (blank = /events)", noLang: true, placeholder: { en: "/events" } },
      { key: "nav_events_hidden", label: "EVENTS — hide from header", noLang: true, bool: true },
      { key: "nav_roster", label: "ROSTER — label" },
      { key: "nav_roster_url", label: "ROSTER — URL (blank = /artists)", noLang: true, placeholder: { en: "/artists" } },
      { key: "nav_roster_hidden", label: "ROSTER — hide from header", noLang: true, bool: true },
      { key: "nav_about", label: "ABOUT — label" },
      { key: "nav_about_url", label: "ABOUT — URL (blank = /about)", noLang: true, placeholder: { en: "/about" } },
      { key: "nav_about_hidden", label: "ABOUT — hide from header", noLang: true, bool: true },
    ],
  },
  {
    id: "shop_menu",
    label: "SHOP DROPDOWN (the 3-column menu under SHOP)",
    items: [
      { key: "shop_menu_col1_title", label: "COLUMN 1 TITLE (default: COLLECTIONS)", placeholder: { en: "COLLECTIONS", pt: "COLEÇÕES" } },
      { key: "shop_menu_col1_hidden", label: "COLUMN 1 — hide", noLang: true, bool: true },
      { key: "shop_menu_col2_title", label: "COLUMN 2 TITLE (default: BY WRESTLER — auto-fills from your roster's wrestlers)", placeholder: { en: "BY WRESTLER", pt: "POR WRESTLER" } },
      { key: "shop_menu_col2_hidden", label: "COLUMN 2 — hide", noLang: true, bool: true },
      { key: "shop_menu_col3_title", label: "COLUMN 3 TITLE (default: BY ARTIST — auto-fills from your roster's musicians)", placeholder: { en: "BY ARTIST", pt: "POR ARTISTA" } },
      { key: "shop_menu_col3_hidden", label: "COLUMN 3 — hide", noLang: true, bool: true },
    ],
  },
  {
    id: "hero",
    label: "HOMEPAGE HERO",
    items: [
      { key: "hero_tagline", label: "TAGLINE", placeholder: { en: "WRESTLING / MUSIC / EVENTS", pt: "WRESTLING / MÚSICA / EVENTOS" } },
      { key: "hero_title", label: "TITLE (use \\n for line break)", multiline: true, placeholder: { en: "WEAR THE\\nCULT.", pt: "VESTE O\\nCULTO." } },
      { key: "hero_sub", label: "SUBLINE", multiline: true, placeholder: { en: "Apparel forged in Porto…", pt: "Roupa forjada no Porto…" } },
      { key: "hero_cta_shop", label: "CTA — SHOP", placeholder: { en: "SHOP THE DROP", pt: "VER A DROP" } },
      { key: "hero_cta_events", label: "CTA — EVENTS", placeholder: { en: "UPCOMING EVENTS", pt: "PRÓXIMOS EVENTOS" } },
    ],
    images: [
      { key: "hero_image", label: "HERO BACKGROUND", rec: "1920×1080 (16:9 landscape)" },
    ],
  },
  {
    id: "sections",
    label: "HOMEPAGE SECTIONS",
    items: [
      { key: "featured_title", label: "FEATURED — TITLE", placeholder: { en: "FEATURED PIECES", pt: "PEÇAS EM DESTAQUE" } },
      { key: "featured_sub", label: "FEATURED — SUB", placeholder: { en: "Latest drops…", pt: "Últimos lançamentos…" } },
      { key: "collections_title", label: "COLLECTIONS — TITLE", placeholder: { en: "SHOP BY COLLECTION", pt: "POR COLEÇÃO" } },
      { key: "events_title", label: "EVENTS — TITLE", placeholder: { en: "UPCOMING EVENTS", pt: "PRÓXIMOS EVENTOS" } },
      { key: "events_sub", label: "EVENTS — SUB", placeholder: { en: "Where the cult meets", pt: "Onde o culto se encontra" } },
      { key: "events_free_label", label: "FREE EVENT BADGE LABEL", placeholder: { en: "FREE", pt: "GRÁTIS" } },
    ],
    images: [
      { key: "collection_wrestling_image", label: "WRESTLING CARD", rec: "800×1200 (2:3 portrait)" },
      { key: "collection_music_image", label: "MUSIC CARD", rec: "800×1200 (2:3 portrait)" },
      { key: "collection_events_image", label: "EVENTS CARD", rec: "800×1200 (2:3 portrait)" },
    ],
  },
  {
    id: "about",
    label: "ABOUT PAGE",
    items: [
      { key: "about_title", label: "TITLE", placeholder: { en: "ABOUT KULTOPIA", pt: "SOBRE A KULTOPIA" } },
      { key: "about_subtitle", label: "SUBTITLE", placeholder: { en: "Apparel from the cult, for the cult.", pt: "Roupa do culto, para o culto." } },
      { key: "about_body_1", label: "PARAGRAPH 1", multiline: true },
      { key: "about_body_2", label: "PARAGRAPH 2 (pink quote)", multiline: true },
      { key: "about_body_3", label: "PARAGRAPH 3", multiline: true },
      { key: "about_stat_1_value", label: "STAT 1 — VALUE (no lang)", noLang: true, placeholder: { en: "12" } },
      { key: "about_stat_1_label", label: "STAT 1 — LABEL", placeholder: { en: "DROPS", pt: "DROPS" } },
      { key: "about_stat_2_value", label: "STAT 2 — VALUE (no lang)", noLang: true, placeholder: { en: "08" } },
      { key: "about_stat_2_label", label: "STAT 2 — LABEL", placeholder: { en: "ARTISTS", pt: "ARTISTAS" } },
      { key: "about_stat_3_value", label: "STAT 3 — VALUE (no lang)", noLang: true, placeholder: { en: "24" } },
      { key: "about_stat_3_label", label: "STAT 3 — LABEL", placeholder: { en: "EVENTS", pt: "EVENTOS" } },
    ],
    images: [
      { key: "about_hero_image", label: "ABOUT HERO", rec: "1600×900 (landscape)" },
      { key: "about_secondary_image", label: "ABOUT SECONDARY", rec: "1200×1500 (4:5 portrait)" },
    ],
  },
  {
    id: "help",
    label: "HELP PAGES (FAQ / Delivery / Contact)",
    items: [
      { key: "nav_help", label: "HELP — nav label", placeholder: { en: "HELP", pt: "AJUDA" } },
      { key: "nav_help_hidden", label: "HIDE HELP MENU?", noLang: true, bool: true },
      { key: "nav_help_faq", label: "DROPDOWN — FAQ link label", placeholder: { en: "FAQ", pt: "FAQ" } },
      { key: "nav_help_delivery", label: "DROPDOWN — Delivery link label", placeholder: { en: "DELIVERY INFO", pt: "INFO DE ENVIO" } },
      { key: "nav_help_contact", label: "DROPDOWN — Contact link label", placeholder: { en: "CONTACT", pt: "CONTACTO" } },

      { key: "help_faq_title", label: "FAQ PAGE — Title", placeholder: { en: "FREQUENTLY ASKED", pt: "PERGUNTAS FREQUENTES" } },
      { key: "help_faq_intro", label: "FAQ PAGE — Intro (above questions)", multiline: true, placeholder: { en: "Most-asked stuff. If your question isn't here, hit us up.", pt: "Perguntas mais comuns. Se não vires a tua, contacta-nos." } },
      { key: "help_faq_body", label: "FAQ PAGE — Q/A body. Format: Q: ... newline A: ... blank line between questions", multiline: true, placeholder: { en: "Q: How long does shipping take?\nA: 1-3 working days in Portugal.\n\nQ: Do you ship abroad?\nA: Yes, EU and UK.", pt: "Q: Quanto tempo demora o envio?\nA: 1-3 dias úteis em Portugal.\n\nQ: Enviam para o estrangeiro?\nA: Sim, UE e Reino Unido." } },

      { key: "help_delivery_title", label: "DELIVERY PAGE — Title", placeholder: { en: "DELIVERY INFO", pt: "INFO DE ENVIO" } },
      { key: "help_delivery_intro", label: "DELIVERY PAGE — Intro", multiline: true, placeholder: { en: "How we ship and when to expect your order.", pt: "Como enviamos e quando esperar a tua encomenda." } },
      { key: "help_delivery_body", label: "DELIVERY PAGE — Body", multiline: true, placeholder: { en: "Portugal: 1-3 working days. EU: 3-7 working days. Free shipping on orders €60+.", pt: "Portugal: 1-3 dias úteis. UE: 3-7 dias úteis. Envio grátis acima de €60." } },

      { key: "help_contact_title", label: "CONTACT PAGE — Title", placeholder: { en: "CONTACT US", pt: "CONTACTA-NOS" } },
      { key: "help_contact_intro", label: "CONTACT PAGE — Intro", multiline: true, placeholder: { en: "Drop us a line. We answer within 1-2 working days.", pt: "Envia-nos mensagem. Respondemos em 1-2 dias úteis." } },
      { key: "help_contact_body", label: "CONTACT PAGE — Body above form (optional)", multiline: true, placeholder: { en: "For order issues, include your order number.", pt: "Para questões de encomenda, inclui o número da encomenda." } },
    ],
  },
  {
    id: "lookbook",
    label: "LOOKBOOK INTRO",
    items: [
      { key: "lookbook_title", label: "TITLE", placeholder: { en: "CAPSULE 02.", pt: "CÁPSULA 02." } },
      { key: "lookbook_sub", label: "SUBTITLE", multiline: true, placeholder: { en: "IN THE RING — shot in Porto…", pt: "NO RINGUE — fotografado no Porto…" } },
    ],
  },
  {
    id: "marquees",
    label: "MARQUEE STRIPS (csv)",
    items: [
      { key: "marquee_main", label: "MARQUEE TEXT — appears on both top + footer (comma-separated)", noLang: true, placeholder: { en: "WRESTLING, MUSIC, EVENTS, PORTO, INVICTA" } },
    ],
  },
  {
    id: "footer",
    label: "FOOTER",
    items: [
      { key: "footer_tag", label: "TAGLINE — line 1", placeholder: { en: "WRESTLING / MUSIC / EVENTS", pt: "WRESTLING / MÚSICA / EVENTOS" } },
      { key: "footer_tag_full", label: "TAGLINE — full sentence (overrides line 1). Blank uses the default with 'Porto'.", multiline: true, placeholder: { en: "WRESTLING / MUSIC / EVENTS. Apparel forged in Lisbon, worn worldwide.", pt: "WRESTLING / MÚSICA / EVENTOS. Vestuário forjado em Lisboa, usado por todo o mundo." } },
      { key: "footer_rights", label: "RIGHTS LINE", placeholder: { en: "© 2026 KULTOPIA APPAREL. ALL RIGHTS RESERVED.", pt: "© 2026 KULTOPIA APPAREL. TODOS OS DIREITOS RESERVADOS." } },
      { key: "newsletter_title", label: "NEWSLETTER TITLE", placeholder: { en: "JOIN THE CULT.", pt: "JUNTA-TE AO CULTO." } },
      { key: "newsletter_sub", label: "NEWSLETTER SUB", placeholder: { en: "Drops, events, and the occasional secret. No spam.", pt: "Drops, eventos e algum segredo. Sem spam." } },
      { key: "social_instagram", label: "INSTAGRAM URL", noLang: true, placeholder: { en: "https://instagram.com/kultopia" } },
      { key: "social_instagram_hidden", label: "HIDE INSTAGRAM ICON", noLang: true, bool: true },
      { key: "social_twitter", label: "TWITTER URL", noLang: true, placeholder: { en: "https://twitter.com/kultopia" } },
      { key: "social_twitter_hidden", label: "HIDE TWITTER ICON", noLang: true, bool: true },
      { key: "social_youtube", label: "YOUTUBE URL", noLang: true, placeholder: { en: "https://youtube.com/@kultopia" } },
      { key: "social_youtube_hidden", label: "HIDE YOUTUBE ICON", noLang: true, bool: true },
    ],
  },
  {
    id: "emails",
    label: "EMAIL TEMPLATES (sent to customers)",
    items: [
      { key: "email_brand_name", label: "BRAND NAME (in subject + logo)", noLang: true, placeholder: { en: "KULTOPIA" } },
      { key: "email_brand_tagline", label: "BRAND TAGLINE (under logo)", placeholder: { en: "WRESTLING · MUSIC · EVENTS", pt: "WRESTLING · MÚSICA · EVENTOS" } },
      { key: "email_footer", label: "EMAIL FOOTER LINE", placeholder: { en: "KULTOPIA APPAREL · kultopiaapparel.com", pt: "KULTOPIA APPAREL · kultopiaapparel.com" } },

      { key: "email_order_subject", label: "ORDER CONFIRMED — SUBJECT (use {order_number})", placeholder: { en: "⚡ Order confirmed — {order_number}", pt: "⚡ Encomenda confirmada — {order_number}" } },
      { key: "email_order_heading", label: "ORDER CONFIRMED — HEADING", placeholder: { en: "Order confirmed · {order_number}", pt: "Encomenda confirmada · {order_number}" } },
      { key: "email_order_intro", label: "ORDER CONFIRMED — INTRO (use {customer})", multiline: true, placeholder: { en: "Thanks {customer}. Your cult merch is on the way.", pt: "Obrigado {customer}. A tua merch do culto está a caminho." } },
      { key: "email_order_shipto", label: "ORDER — \"SHIPPING TO:\" LABEL", placeholder: { en: "Shipping to:", pt: "Envio para:" } },

      { key: "email_shipped_subject", label: "SHIPPED — SUBJECT (use {order_number})", placeholder: { en: "📦 Your Kultopia order has shipped — {order_number}", pt: "📦 A tua encomenda Kultopia foi enviada — {order_number}" } },
      { key: "email_shipped_heading", label: "SHIPPED — HEADING", placeholder: { en: "On its way · {order_number}", pt: "A caminho · {order_number}" } },
      { key: "email_shipped_intro", label: "SHIPPED — INTRO (use {customer})", multiline: true, placeholder: { en: "Hi {customer} — your order just shipped from Porto.", pt: "Olá {customer} — a tua encomenda acabou de sair do Porto." } },
      { key: "email_shipped_signoff", label: "SHIPPED — SIGN-OFF", placeholder: { en: "We'll be here for the next drop.", pt: "Estaremos cá para o próximo drop." } },
      { key: "email_shipped_tracking_label", label: "SHIPPED — \"TRACKING NUMBER\" LABEL", placeholder: { en: "Tracking number", pt: "Número de rastreamento" } },

      { key: "email_ticket_subject", label: "TICKET — SUBJECT (use {event_title})", placeholder: { en: "⚡ Your ticket — {event_title}", pt: "⚡ O teu bilhete — {event_title}" } },
      { key: "email_ticket_heading", label: "TICKET — HEADING", placeholder: { en: "You're in.", pt: "Estás dentro." } },
      { key: "email_ticket_intro", label: "TICKET — INTRO (use {customer}, {event_title})", multiline: true, placeholder: { en: "Hi {customer}, here's your ticket for {event_title}.", pt: "Olá {customer}, aqui está o teu bilhete para {event_title}." } },

      { key: "email_abandoned_subject", label: "ABANDONED CART — SUBJECT", placeholder: { en: "Still thinking? Your cart is waiting ⚡", pt: "Ainda a pensar? O teu carrinho está à espera ⚡" } },
      { key: "email_abandoned_heading", label: "ABANDONED CART — HEADING", placeholder: { en: "Your Kultopia cart", pt: "O teu carrinho Kultopia" } },
      { key: "email_abandoned_intro", label: "ABANDONED CART — INTRO (use {customer})", multiline: true, placeholder: { en: "Hi {customer} — these pieces are still in your cart.", pt: "Olá {customer} — estas peças continuam no teu carrinho." } },
      { key: "email_abandoned_cta", label: "ABANDONED CART — CTA BUTTON", placeholder: { en: "Resume checkout", pt: "Continuar compra" } },
      { key: "email_abandoned_promo", label: "ABANDONED CART — PROMO LINE (use {code}, {percent})", placeholder: { en: "Use code {code} for {percent}% off", pt: "Usa o código {code} para {percent}% de desconto" } },
    ],
    images: [
      { key: "email_logo_url", label: "EMAIL LOGO (replaces wordmark; leave empty for default KULTOPIA wordmark)", rec: "600×180 transparent PNG/JPG" },
    ],
  },
];

export const AdminSiteTab = () => {
  const { refresh: refreshSite } = useSite();
  const [form, setForm] = useState({});
  const [open, setOpen] = useState({ hero: true });
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/site-content").then(({ data }) => {
      const d = data || {};
      // Mirror non-suffixed keys into _en slot so the form inputs (which read _en) show them.
      const mirrors = [
        "site_locked",
        "site_lock_password",
        "drop_countdown_enabled",
        "drop_countdown_date",
        "drop_countdown_link",
        "nav_shop_url", "nav_shop_hidden",
        "nav_tickets_url", "nav_tickets_hidden",
        "nav_events_url", "nav_events_hidden",
        "nav_roster_url", "nav_roster_hidden",
        "nav_about_url", "nav_about_hidden",
        "nav_help_hidden",
        "shop_menu_col1_hidden",
        "shop_menu_col2_hidden",
        "shop_menu_col3_hidden",
        "marquee_main",
        "marquee_footer",
        "social_instagram",
        "social_instagram_hidden",
        "social_twitter",
        "social_twitter_hidden",
        "social_youtube",
        "social_youtube_hidden",
        "about_stat_1_value",
        "about_stat_2_value",
        "about_stat_3_value",
        "email_brand_name",
      ];
      mirrors.forEach((k) => {
        if (d[k] !== undefined && d[k] !== null && d[`${k}_en`] === undefined) {
          // For booleans, keep the actual boolean so the toggle UI works; non-bools become string for text input.
          d[`${k}_en`] = typeof d[k] === "boolean" ? d[k] : d[k];
        }
      });
      setForm(d);
    }).catch(() => {});
  }, []);

  const setField = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const setImage = (key) => (urls) => setForm({ ...form, [key]: urls[0] || "" });

  const save = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      // Normalize special non-suffixed keys + booleans
      const payload = { ...form };
      const normNoLang = [
        "site_locked",
        "site_lock_password",
        "drop_countdown_enabled",
        "drop_countdown_date",
        "drop_countdown_link",
        "nav_shop_url", "nav_shop_hidden",
        "nav_tickets_url", "nav_tickets_hidden",
        "nav_events_url", "nav_events_hidden",
        "nav_roster_url", "nav_roster_hidden",
        "nav_about_url", "nav_about_hidden",
        "nav_help_hidden",
        "shop_menu_col1_hidden",
        "shop_menu_col2_hidden",
        "shop_menu_col3_hidden",
        "marquee_main",
        "marquee_footer",
        "social_instagram",
        "social_instagram_hidden",
        "social_twitter",
        "social_twitter_hidden",
        "social_youtube",
        "social_youtube_hidden",
        "about_stat_1_value",
        "about_stat_2_value",
        "about_stat_3_value",
        "email_brand_name",
      ];
      normNoLang.forEach((k) => {
        if (payload[`${k}_en`] !== undefined) {
          payload[k] = payload[`${k}_en`];
        }
      });
      // Coerce booleans for any toggle field (site_locked, drop_countdown_enabled, all *_hidden flags)
      ["site_locked", "drop_countdown_enabled",
       "nav_shop_hidden", "nav_tickets_hidden", "nav_events_hidden",
       "nav_roster_hidden", "nav_about_hidden", "nav_help_hidden",
       "shop_menu_col1_hidden", "shop_menu_col2_hidden", "shop_menu_col3_hidden",
       "social_instagram_hidden", "social_twitter_hidden", "social_youtube_hidden"].forEach((k) => {
        if (typeof payload[k] === "string") {
          payload[k] = ["true", "1", "yes", "on"].includes(payload[k].trim().toLowerCase());
        } else if (payload[k] === undefined) {
          // leave it absent so the backend doesn't overwrite an existing value
        } else {
          payload[k] = Boolean(payload[k]);
        }
      });
      await api.patch("/admin/site-content", payload);
      setSaved(true);
      refreshSite();
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="admin-site-section">
      <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-4">/ SITE COPY & IMAGES</p>
      <p className="text-kult-muted text-sm mb-6">
        Everything below overrides the built-in defaults. Empty fields fall back to the defaults shown as placeholders.
      </p>
      <form onSubmit={save} className="space-y-3" data-testid="admin-site-form">
        {SECTIONS.map((sec) => (
          <details key={sec.id} open={!!open[sec.id]} className="border border-kult-border">
            <summary
              onClick={(e) => {
                e.preventDefault();
                setOpen({ ...open, [sec.id]: !open[sec.id] });
              }}
              className="cursor-pointer px-5 py-3 bg-kult-black2 font-mono text-xs tracking-[0.3em] text-kult-yellow flex items-center gap-2 list-none"
              data-testid={`admin-site-section-${sec.id}`}
            >
              {open[sec.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {sec.label}
            </summary>
            {open[sec.id] && (
              <div className="p-5 space-y-4">
                {sec.items.map((it) => (
                  <div key={it.key} className="border-l-2 border-kult-border pl-4">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted mb-2">{it.label}</p>
                    {it.bool ? (
                      <BoolToggle
                        value={form[`${it.key}_en`]}
                        onChange={(v) => setForm({ ...form, [`${it.key}_en`]: v })}
                        testId={`site-${it.key}-toggle`}
                        hint={it.hint}
                      />
                    ) : it.noLang ? (
                      it.multiline ? (
                        <Textarea value={form[`${it.key}_en`] || ""} onChange={setField(`${it.key}_en`)} placeholder={it.placeholder?.en} testId={`site-${it.key}-en`} />
                      ) : (
                        <Input value={form[`${it.key}_en`] || ""} onChange={setField(`${it.key}_en`)} placeholder={it.placeholder?.en} testId={`site-${it.key}-en`} />
                      )
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {it.multiline ? (
                          <>
                            <Textarea label="EN" value={form[`${it.key}_en`] || ""} onChange={setField(`${it.key}_en`)} placeholder={it.placeholder?.en} testId={`site-${it.key}-en`} />
                            <Textarea label="PT" value={form[`${it.key}_pt`] || ""} onChange={setField(`${it.key}_pt`)} placeholder={it.placeholder?.pt} testId={`site-${it.key}-pt`} />
                          </>
                        ) : (
                          <>
                            <Input label="EN" value={form[`${it.key}_en`] || ""} onChange={setField(`${it.key}_en`)} placeholder={it.placeholder?.en} testId={`site-${it.key}-en`} />
                            <Input label="PT" value={form[`${it.key}_pt`] || ""} onChange={setField(`${it.key}_pt`)} placeholder={it.placeholder?.pt} testId={`site-${it.key}-pt`} />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {(sec.images || []).map((im) => (
                  <div key={im.key} className="border-l-2 border-kult-border pl-4">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted mb-2">{im.label}</p>
                    <ImageUpload value={form[im.key] ? [form[im.key]] : []} onChange={setImage(im.key)} max={1} testIdPrefix={`site-${im.key}`} recommendedSize={im.rec || ""} />
                  </div>
                ))}
              </div>
            )}
          </details>
        ))}
        {error && <p className="font-mono text-xs text-kult-pink">{error}</p>}
        <div className="sticky bottom-0 bg-kult-black border-t border-kult-border py-4 flex items-center gap-4">
          <button type="submit" disabled={submitting} className="bg-kult-pink text-black font-display text-xl py-3 px-6 hover:bg-kult-yellow disabled:opacity-50 inline-flex items-center gap-2" data-testid="site-submit">
            <Save size={18} /> {submitting ? "SAVING..." : "SAVE ALL"}
          </button>
          {saved && <span className="font-mono text-xs text-kult-yellow tracking-[0.25em]" data-testid="site-saved">SAVED ⚡</span>}
        </div>
      </form>
    </section>
  );
};

function Input({ label, testId, ...props }) {
  return (
    <label className="block">
      {label && <span className="font-mono text-[9px] tracking-[0.3em] text-kult-muted block mb-1">{label}</span>}
      <input {...props} data-testid={testId} className="w-full bg-transparent border border-kult-border px-3 py-2 font-mono text-sm focus:border-kult-pink outline-none" />
    </label>
  );
}
function Textarea({ label, testId, ...props }) {
  return (
    <label className="block">
      {label && <span className="font-mono text-[9px] tracking-[0.3em] text-kult-muted block mb-1">{label}</span>}
      <textarea rows={3} {...props} data-testid={testId} className="w-full bg-transparent border border-kult-border px-3 py-2 font-mono text-sm focus:border-kult-pink outline-none" />
    </label>
  );
}

function BoolToggle({ value, onChange, testId, hint }) {
  // Accept boolean OR loose string ("true"/"false") values for backwards compatibility.
  const isOn = value === true || value === "true" || value === 1 || value === "1";
  return (
    <div>
      <button
        type="button"
        onClick={() => onChange(!isOn)}
        data-testid={testId}
        aria-pressed={isOn}
        className={`relative inline-flex items-center gap-3 px-1 py-1 border-2 transition-colors ${isOn ? "border-kult-pink bg-kult-pink/10" : "border-kult-border bg-transparent"}`}
      >
        <span className={`relative inline-block w-12 h-6 rounded-full transition-colors ${isOn ? "bg-kult-pink" : "bg-kult-border"}`}>
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-black transition-transform ${isOn ? "translate-x-6" : "translate-x-0"}`} />
        </span>
        <span className={`font-display text-lg ${isOn ? "text-kult-pink" : "text-kult-muted"}`}>
          {isOn ? "ON" : "OFF"}
        </span>
      </button>
      {hint && <p className="font-mono text-[10px] text-kult-muted mt-2 leading-relaxed">{hint}</p>}
    </div>
  );
}
